import React, { useState, useEffect, useCallback } from "react";
import s from "./ticketOnEvent.module.scss";
import money from "../../Images/Money.svg";
import { TicketType } from "../../../../types/types";
import plus from "../../Images/plus.png";
import minus from "../../Images/minus.png";

interface TicketOnEventProps {
  editableTicketTypes: TicketType[] | null;
  editableTicketTypesId: (number | undefined)[] | undefined;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  hasError: boolean;
}

const normalizeNonNegativeInt = (raw: string): number => {
  if (raw === "") return 0;

  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return 0;

  return Math.max(0, parsed);
};

export const TicketOnEvent: React.FC<TicketOnEventProps> = ({
  editableTicketTypes,
  editableTicketTypesId,
  setFormData,
  hasError,
}) => {
  const [tickets, setTickets] = useState<TicketType[]>(
    editableTicketTypes || [
      {
        name: "Стандарт",
        description: "Базовый билет без прохода в VIP зону",
        price: 2000,
        max_count: 52,
        sorter: 10,
      },
    ]
  );

  const [ticketErrors, setTicketErrors] = useState<
    Array<{
      name?: string;
      price?: string;
      max_count?: string;
    }>
  >([]);

  const [touched, setTouched] = useState<
    Array<{
      name?: boolean;
      price?: boolean;
      max_count?: boolean;
    }>
  >([]);

  // const [width] = useWindowSize();

  const validateTickets = useCallback(
    (ticketsToValidate: TicketType[], checkAll: boolean) => {
      return ticketsToValidate.map((ticket, index) => {
        const errors: { name?: string; price?: string; max_count?: string } =
          {};

        const shouldValidateField = (field: keyof (typeof touched)[0]) =>
          checkAll || (touched[index] && touched[index][field]);

        if (shouldValidateField("name") && !ticket.name.trim()) {
          errors.name = "Название обязательно";
        }
        if (shouldValidateField("price") && ticket.price <= 0) {
          errors.price = "Цена должна быть больше 0";
        }
        if (shouldValidateField("max_count") && ticket.max_count <= 0) {
          errors.max_count = "Количество должно быть больше 0";
        }

        return errors;
      });
    },
    [touched]
  );

  useEffect(() => {
    const errors = validateTickets(tickets, hasError);
    setTicketErrors(errors);
    setFormData((prev: any) => ({
      ...prev,
      ticket_types: tickets.map((ticket, index) => ({
        ...ticket,
        id: editableTicketTypesId && editableTicketTypesId[index],
      })),
    }));
  }, [
    tickets,
    setFormData,
    hasError,
    touched,
    editableTicketTypesId,
    validateTickets,
  ]);

  const handleAddTicket = () => {
    if (tickets.length >= 3) return;
    const newTickets = [
      ...tickets,
      {
        name: "",
        description: "",
        price: 0,
        max_count: 0,
        sorter: 10 + tickets.length * 10,
      },
    ];
    setTickets(newTickets);
    setTouched([...touched, {}]);
    setTicketErrors(validateTickets(newTickets, hasError));
  };

  const handleRemoveTicket = (index: number) => {
    const newTickets = tickets.filter((_, i) => i !== index);
    setTickets(newTickets);
    const newTouched = touched.filter((_, i) => i !== index);
    setTouched(newTouched);
    setTicketErrors(validateTickets(newTickets, hasError));
  };

  const handleTicketChange = (
    index: number,
    field: keyof TicketType,
    value: any
  ) => {
    const newTickets = [...tickets];
    if (field === "max_count") {
      newTickets[index] = {
        ...newTickets[index],
        [field]: Math.max(
          0,
          typeof value === "number" ? value : parseInt(value) || 0
        ),
      };
    } else {
      newTickets[index] = { ...newTickets[index], [field]: value };
    }
    setTickets(newTickets);

    // Помечаем поле как "тронутое"
    const newTouched = [...touched];
    if (!newTouched[index]) newTouched[index] = {};
    newTouched[index] = { ...newTouched[index], [field]: true };
    setTouched(newTouched);
  };

  const handleBlur = (index: number, field: keyof TicketType) => {
    const newTouched = [...touched];
    if (!newTouched[index]) newTouched[index] = {};
    newTouched[index] = { ...newTouched[index], [field]: true };
    setTouched(newTouched);
  };

  const hasTicketErrors = ticketErrors.some(
    (error) => error.name || error.price || error.max_count
  );

  return (
    <div
      className={`${s.ticketSection} ${
        hasError || hasTicketErrors ? s.errorSection : ""
      }`}
    >
      <h2 className={s.sectionTitle}>Билеты на событие</h2>
      <div className={s.container}>
        {tickets.map((ticket, index) => (
          <div style={{ display: "flex" }} key={index}>
            <div className={s.ticket}>
              <div className={s.ticketContainer}>
                <div className={s.ticketInfo}>Название категории*</div>
                <input
                  className={`${s.ticketInputActive} ${
                    ticketErrors[index]?.name ? s.errorBorder : ""
                  }`}
                  placeholder="Название категории"
                  value={ticket.name}
                  style={{ color: "#262429" }}
                  onChange={(e) =>
                    handleTicketChange(index, "name", e.target.value)
                  }
                  onBlur={() => handleBlur(index, "name")}
                />
                {ticketErrors[index]?.name && (
                  <div className={s.error}>{ticketErrors[index].name}</div>
                )}
              </div>

              <div className={s.ticketContainer}>
                <div className={s.ticketInfo}>Описание категории</div>
                <input
                  className={s.ticketInputActive}
                  style={{ color: "#262429" }}
                  placeholder="Описание категории"
                  value={ticket.description}
                  onChange={(e) =>
                    handleTicketChange(index, "description", e.target.value)
                  }
                />
              </div>

              <div className={s.ticketRow}>
                <div
                  className={`${s.SimpleTicket} ${
                    ticketErrors[index]?.price ? s.errorBorder : ""
                  }`}
                >
                  <label className={s.ticketLabel}>Цена</label>
                  <div className={s.SimpleTicket_price}>
                    <input
                      className={`${s.ticketInput} ${
                        ticketErrors[index]?.price ? s.errorBorder : ""
                      }`}
                      placeholder="2000"
                      style={{ color: "#262429" }}
                      type="number"
                      value={ticket.price === 0 ? "" : ticket.price}
                      onChange={(e) =>
                        handleTicketChange(
                          index,
                          "price",
                          normalizeNonNegativeInt(e.target.value)
                        )
                      }
                      onBlur={() => handleBlur(index, "price")}
                    />
                    <img className={s.moneyIcon} src={money} alt="Рублей" />
                  </div>
                </div>
                <div
                  className={`${s.SimpleTicket} ${
                    ticketErrors[index]?.max_count ? s.errorBorder : ""
                  }`}
                >
                  <label className={s.ticketLabel}>Количество</label>
                  <div className={s.buttonContainer}>
                    <button
                      className={s.decreaseButton}
                      onClick={() => {
                        handleTicketChange(
                          index,
                          "max_count",
                          (ticket.max_count || 0) - 1
                        );
                        handleBlur(index, "max_count");
                      }}
                    >
                      <img src={minus} alt="minus" />
                    </button>
                    <input
                      className={s.ticketInput}
                      type="number"
                      value={ticket.max_count === 0 ? "" : ticket.max_count}
                      onChange={(e) =>
                        handleTicketChange(
                          index,
                          "max_count",
                          normalizeNonNegativeInt(e.target.value)
                        )
                      }
                      style={{ color: "#262429" }}
                      onBlur={() => handleBlur(index, "max_count")}
                    />
                    <button
                      className={s.increaseButton}
                      onClick={() => {
                        handleTicketChange(
                          index,
                          "max_count",
                          (ticket.max_count || 0) + 1
                        );
                        handleBlur(index, "max_count");
                      }}
                    >
                      <img src={plus} alt="plus" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={s.Butons}>
              {index >= 0 && index !== tickets.length - 1 && (
                <div
                  className={s.addButton}
                  onClick={() => handleRemoveTicket(index)}
                >
                  <img src={minus} alt="minus" />
                </div>
              )}
              {index === tickets.length - 1 && (
                <>
                  {index > 0 && (
                    <div
                      className={
                        index > 0 && index === 1
                          ? s.addButton_duble_up
                          : s.addButton
                      }
                      onClick={() => handleRemoveTicket(index)}
                    >
                      <img src={minus} alt="minus" />
                    </div>
                  )}
                  {index === tickets.length - 1 && tickets.length < 3 && (
                    <div
                      className={
                        index > 0 ? s.addButton_duble_down : s.addButton
                      }
                      onClick={handleAddTicket}
                    >
                      <img src={plus} alt="plus" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
